import React, { useState, useEffect, useCallback } from "react";
import moment from 'moment';

import { Grid, Typography, InputLabel, MenuItem, Select, Chip } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { useParams, useHistory } from "react-router-dom";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

//Components
import CMLabel from '../../components/atoms/CMLabel';
import CMButton from '../../components/atoms/CMButton';
import CMTextField from "../../components/atoms/CMTextField";
import CMIconButton from '../../components/atoms/CMIconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

//Constants
import { MomentLocaleEs } from '../../constants/MomentLocaleEs';

//Services
import * as OrdenesService from '../../services/OrdenesService';
import * as FinanzaService from '../../services/FinanzaService';

import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
const MySwal = withReactContent(Swal);


const OrdenesPendientesAprobarFinanzaVer = () => {
  const history = useHistory();

  const [rowsdata, setRowsdata] = useState([]);
  const [rowsgrid, setRowsgrid] = useState([]);
  const [comment, setComment] = useState('');
  const [selectedOption, setSelectedOption] = React.useState('');
  const [cargando, setCargando] = React.useState(false);
  const [isDisabled, setIsDisabled] = React.useState(false);
  const [disabledComment, setDisabledComment] = useState(false);

  const [analisis, setAnalisis] = useState(null);
  const [loadingAnalisis, setLoadingAnalisis] = useState(false);

  const user = JSON.parse(window.localStorage.getItem("user"));

  // ─── Selección aprobar/rechazar ───────────────────────────────────────────────
  const handleChange = (event) => setSelectedOption(event.target.value);

  useEffect(() => {
    if (selectedOption === 'aprobar') {
      setComment("OK");
      setDisabledComment(true);
    } else if (selectedOption === 'rechazar') {
      setComment('');
      setDisabledComment(false);
    }
  }, [selectedOption]);

  // ─── Fake mutation DataGrid ───────────────────────────────────────────────────
  const useFakeMutation = () => {
    return React.useCallback(
      (user) =>
        new Promise((resolve, reject) =>
          setTimeout(() => {
            if (user.name?.trim() === '') {
              reject(new Error("Error al guardar el usuario: el nombre no puede estar vacío"));
            } else {
              resolve({ ...user, name: user.name?.toUpperCase() });
            }
          }, 200),
        ),
      [],
    );
  };
  const mutateRow = useFakeMutation();
  const [snackbar, setSnackbar] = useState(null);
  const [dataresponse, setDataresponse] = useState(null);
  const handleCloseSnackbar = () => setSnackbar(null);
  const handleProcessRowUpdateError = React.useCallback((error) => {
    setSnackbar({ children: error.message, severity: 'error' });
  }, []);

  // ─── Carga inicial OC ─────────────────────────────────────────────────────────
  const { ordenid } = useParams();
  useEffect(() => {
    OrdenesService.getOCID(setRowsdata, ordenid, user.token);
  }, []);

  let idCounter = 0;
  useEffect(() => {
    if (rowsdata.oc_detalles) {
      const rowsWithId = rowsdata.oc_detalles.map(row => ({ ...row, id: idCounter++ }));
      setRowsgrid(rowsWithId);
    }
  }, [rowsdata]);

  useEffect(() => {
    if (rowsgrid) {
      setIsDisabled(rowsdata.nombre_aprobador_finanzas === null ? false : true);
    }
  }, [rowsgrid]);

  // ─── Carga análisis ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!rowsdata || !rowsdata.id_oc_cabecera) return;
    const docEntry = rowsdata.id_documento_borrador || rowsdata.id_oc_cabecera;
    setLoadingAnalisis(true);
    FinanzaService.getAnalisisPedidos(docEntry, user.token)
      .then(data => setAnalisis(data))
      .finally(() => setLoadingAnalisis(false));
  }, [rowsdata.id_oc_cabecera]);

  // ─── Helpers ──────────────────────────────────────────────────────────────────
  const formatCurrency = (value, moneda) => {
    const currency = moneda === 'US$' ? 'USD' : 'PEN';
    return value.toLocaleString('es-PE', { style: 'currency', currency });
  };

  const colorVariacion = (pct) => pct > 0 ? '#e67e22' : '#c0392b';

  // Badge de conversión aplicada
  const BadgeConversion = ({ label }) => (
    <Chip label={label} size="small" sx={{ backgroundColor: '#fff3cd', color: '#856404', fontSize: '11px', ml: 1 }} />
  );

  // ─── Columnas artículos ───────────────────────────────────────────────────────
  const columns = [
    { field: 'codigo_articulo', headerName: 'Codigo Árticulo', width: 120, align: 'center', headerAlign: 'center' },
    { field: 'nombre_articulo', headerName: 'Árticulo', width: 480, headerAlign: 'center' },
    { field: 'unidad_medida', headerName: 'Unidad Medida', width: 180, align: 'center', headerAlign: 'center' },
    { field: 'cantidad_pedida', headerName: 'Cantidad Pedida', width: 150, align: 'center', headerAlign: 'center' },
    {
      field: 'fecha_entrega', headerName: 'Fecha Acuerdo', type: 'date', width: 140, align: 'center', headerAlign: 'center',
      valueFormatter: (params) => moment(params.value).format('DD/MM/YYYY')
    },
    {
      field: 'tipo_igv', headerName: 'Tipo IGV', width: 180, align: 'center', headerAlign: 'center',
      valueGetter: (params) => params.row.include_igv === 'EXONERADO' ? 'EXONERADO' : params.row.include_igv === 'IGV' ? 'IGV 18%' : 'IGV 10%',
    },
    {
      field: 'valor_sin_igv', headerName: 'Precio sin IGV', width: 180, align: 'center', headerAlign: 'center',
      valueFormatter: (params) => formatCurrency(params.value, rowsdata.moneda),
      valueGetter: (params) => {
        const t = params.row.precio_unitario;
        return params.row.include_igv === 'EXONERADO' ? t : params.row.include_igv === 'IGV' ? t / 1.18 : t / 1.10;
      },
    },
    { field: 'precio_unitario', headerName: 'Precio Unitario', width: 150, align: 'center', headerAlign: 'center', valueFormatter: (params) => formatCurrency(params.value, rowsdata.moneda) },
    {
      field: 'igv', headerName: 'IGV', width: 180, align: 'center', headerAlign: 'center',
      valueFormatter: (params) => formatCurrency(params.value, rowsdata.moneda),
      valueGetter: (params) => {
        const t = params.row.total_articulo;
        return params.row.include_igv === 'EXONERADO' ? 0 : params.row.include_igv === 'IGV' ? t - t / 1.18 : t - t / 1.10;
      },
    },
    { field: 'total_articulo', headerName: 'Total Árticulo', width: 150, align: 'center', headerAlign: 'center', valueFormatter: (params) => formatCurrency(params.value, rowsdata.moneda) },
    { field: 'codigo_ceco', headerName: 'Codigo Ceco', width: 120, align: 'center', headerAlign: 'center' },
    { field: 'codigo_seccion', headerName: 'Codigo Sección', width: 120, align: 'center', headerAlign: 'center' },
    { field: 'codigo_departamente', headerName: 'Codigo departamento', width: 170, align: 'center', headerAlign: 'center' },
    { field: 'codigo_ceco_concat', headerName: 'Ceco', width: 480, align: 'center', headerAlign: 'center' },
    { field: 'codigo_almacen_sap', headerName: 'Codigo Almacen', width: 170, align: 'center', headerAlign: 'center' },
  ];

  // ─── Columnas servicios ───────────────────────────────────────────────────────
  const columns2 = [
    { field: 'descripcion', headerName: 'Descripción', width: 650, align: 'center', headerAlign: 'center' },
    {
      field: 'fecha_entrega', headerName: 'Fecha Entrega', width: 180, align: 'center', headerAlign: 'center',
      valueFormatter: (params) => params.value ? moment(params.value).format('DD/MM/YYYY') : '00/00/0000'
    },
    { field: 'codigo_ceco', headerName: 'Codigo Ceco', width: 120, align: 'center', headerAlign: 'center' },
    { field: 'codigo_seccion', headerName: 'Codigo Sección', width: 120, align: 'center', headerAlign: 'center' },
    { field: 'codigo_departamente', headerName: 'Codigo departamento', width: 170, align: 'center', headerAlign: 'center' },
    { field: 'codigo_ceco_concat', headerName: 'Ceco', width: 480, align: 'center', headerAlign: 'center' },
    { field: 'nombre_cuenta_mayor', headerName: 'Nombre Cuenta Mayor', width: 360, align: 'center', headerAlign: 'center' },
    { field: 'numero_cuenta_mayor', headerName: 'Numero Cuenta Mayor', width: 180, align: 'center', headerAlign: 'center' },
    { field: 'nombreProyecto', headerName: 'Proyecto', width: 180, align: 'center', headerAlign: 'center' },
    { field: 'fiProject', headerName: 'FiProject', width: 180, align: 'center', headerAlign: 'center' },
    { field: 'nombreEtapa', headerName: 'Etapa', width: 180, align: 'center', headerAlign: 'center' },
    {
      field: 'tipo_igv', headerName: 'Tipo IGV', width: 180, align: 'center', headerAlign: 'center',
      valueGetter: (params) => params.row.include_igv === 'EXONERADO' ? 'EXONERADO' : params.row.include_igv === 'IGV' ? 'IGV 18%' : 'IGV 10%',
    },
    {
      field: 'valor_sin_igv', headerName: 'Precio sin IGV', width: 180, align: 'center', headerAlign: 'center',
      valueFormatter: (params) => formatCurrency(params.value, rowsdata.moneda),
      valueGetter: (params) => {
        const t = params.row.total_servicio;
        return params.row.include_igv === 'EXONERADO' ? t : params.row.include_igv === 'IGV' ? t / 1.18 : t / 1.10;
      },
    },
    {
      field: 'igv', headerName: 'IGV', width: 180, align: 'center', headerAlign: 'center',
      valueFormatter: (params) => formatCurrency(params.value, rowsdata.moneda),
      valueGetter: (params) => {
        const t = params.row.total_servicio;
        return params.row.include_igv === 'EXONERADO' ? 0 : params.row.include_igv === 'IGV' ? t - t / 1.18 : t - t / 1.10;
      },
    },
    { field: 'total_servicio', headerName: 'Precio', width: 180, align: 'center', headerAlign: 'center', valueFormatter: (params) => formatCurrency(params.value, rowsdata.moneda) },
  ];

  // ─── Actualización filas ──────────────────────────────────────────────────────
  const processRowUpdate = useCallback(
    async (newRow) => {
      const response = await mutateRow(newRow);
      setDataresponse(response);
      setSnackbar({ children: 'Guardo exitosamente', severity: 'success' });
      return response;
    },
    [mutateRow],
  );

  useEffect(() => {
    if (dataresponse) {
      const { id_oc_detalle, codigo_articulo, unidad_medida, cantidad_pedida, cantidad_entregada, cantidad_recibida, fecha_entrega, fecha_recepcion, fecha_termino, precio_unitario, total_articulo } = dataresponse;
      if (!cantidad_recibida) alert('Debe ingresar la cantidad recibida');
      if (!fecha_recepcion) alert('Debe una fecha de entrega');
      const oldDevices = [...rowsgrid];
      const rowDeviceIndex = oldDevices.findIndex((dev) => dev.id_oc_detalle === id_oc_detalle);
      oldDevices[rowDeviceIndex] = {
        ...oldDevices[rowDeviceIndex],
        id_oc_detalle, codigo_articulo, unidad_medida, cantidad_pedida, cantidad_entregada,
        cantidad_recibida, fecha_entrega, fecha_recepcion, fecha_termino, precio_unitario, total_articulo
      };
      setRowsgrid(oldDevices);
    }
  }, [dataresponse]);

  // ─── Registrar aprobación/rechazo ─────────────────────────────────────────────
  const hanbleEntregada = () => {
    if (!selectedOption) {
      Swal.fire(
        rowsdata.tipo === 'Articulo' ? 'Debe seleccionar un estado de la Orden de Compra' : 'Debe seleccionar un estado de la Orden de Servicio',
        '', 'error'
      );
      return;
    }
    Swal.fire({
      title: '¿Estado conforme en ' + selectedOption + '?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Aceptar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        setCargando(true);
        setIsDisabled(true);
        const datosEstado = {
          id_oc_cabecera: rowsdata.id_oc_cabecera,
          id_aprobador_finanzas: user.id_usuario,
          comentario: comment,
          status: selectedOption === 'aprobar' ? 'ardApproved' : 'ardNotApproved',
          remarks: selectedOption === 'aprobar' ? 'Approved - OK' : comment
        };
        FinanzaService.CrearOCEstado(datosEstado, selectedOption, setCargando, user.token).then(() => {
          Swal.fire({ title: 'Se registró correctamente!', icon: 'success', confirmButtonText: 'OK' })
            .then((result) => {
              if (result.isConfirmed) {
                history.push(`/ordenespendientesaprobacionfinanzas`);
                history.go(0);
              }
            });
        });
      }
    });
  };

  const handleComentarioChange = e => setComment(e.target.value);
  const handleClickRegresar = () => {
    history.push(`/ordenespendientesaprobacionfinanzas`);
    history.go(0);
  };

  const dataGridSx = {
    height: "100%",
    "& .MuiDataGrid-window": {
      overflowX: 'scroll',
      "&::-webkit-scrollbar": { width: "0.4em" },
      "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
      "&::-webkit-scrollbar-thumb": { backgroundColor: "#888" },
      "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
    },
  };

  const hayAlertas = analisis?.resumenAlertas?.lineasConAlerta > 0;

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Grid width={'100%'} m="auto">
      <Grid container justifyContent="flex-start" alignItems="center" spacing={2} sx={{ py: 1 }}>

        {/* ENCABEZADO */}
        <Grid container item xs={12} sm={12}>
          <Grid item sm={6}>
            <CMLabel sx={{ margin: 2 }} fontSize='28px' color="black">
              <b>Orden de {rowsdata.tipo === 'Articulo' ? 'Compra' : 'Servicio'}</b>
            </CMLabel>
          </Grid>
          <Grid item sm={6} container justifyContent="flex-end" alignItems="center">
            <Grid container xs={2} justifyContent="flex-end" alignItems="center">
              <Grid item>
                <CMIconButton disableRipple size="large" sx={{ color: "primary.main", display: "block" }} onClick={handleClickRegresar}>
                  <ArrowBackIcon />
                  <Typography sx={{ lineHeight: 0 }}>Regresar</Typography>
                </CMIconButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* PROVEEDOR / SOLICITANTE / IMPORTE */}
        <Grid container item xs={12} sm={12}>
          <Grid item sm={5.5}>
            <CMLabel sx={{ margin: 2 }} fontSize='20px' color="black"><b>Proveedor</b></CMLabel>
            <hr />
            <Grid item sm={12}><CMLabel fontSize='16px' color='black'><b>Nombre :</b> {rowsdata.nombre_proveedor}</CMLabel></Grid>
            <Grid item sm={12}><CMLabel fontSize='16px' color='black'><b>RUC :</b> {rowsdata.ruc_proveedor}</CMLabel></Grid>
            <CMLabel sx={{ margin: 2 }} fontSize='20px' color="black"><b>Detalle Orden de {rowsdata.tipo === 'Articulo' ? 'Compra' : 'Servicio'}</b></CMLabel>
            <hr />
            <Grid item sm={12}><CMLabel fontSize='16px' color='black'><b>OC Id :</b> {rowsdata.id_documento_borrador === null || rowsdata.id_documento_borrador === '' ? '000' + rowsdata.id_oc_cabecera : rowsdata.id_documento_borrador}</CMLabel></Grid>
            <Grid item sm={12}><CMLabel fontSize='16px' color='black'><b>Estado :</b> {rowsdata.estado}</CMLabel></Grid>
            <Grid item sm={12}><CMLabel fontSize='16px' color='black'><b>OC Estado :</b> {rowsdata.ultimo_nombre_oc_estado}</CMLabel></Grid>
            <Grid item sm={12}><CMLabel fontSize='16px' color='black'><b>Motivo :</b> {rowsdata.motivo_solicitud}</CMLabel></Grid>
            <Grid item sm={12}><CMLabel fontSize='16px' color='black'><b>Observación :</b> {rowsdata.observaciones}</CMLabel></Grid>
            <Grid item sm={12}><CMLabel fontSize='16px' color='black'><b>Condición de Pago :</b> {rowsdata.condic_pago}</CMLabel></Grid>
            <Grid item sm={12}><CMLabel fontSize='16px' color='black'><b>Fecha Contable :</b> {moment(rowsdata.fecha_contable).format("DD-MM-YYYY")}</CMLabel></Grid>
            <Grid item sm={12}><CMLabel fontSize='16px' color='black'><b>Fecha Entrega :</b> {moment(rowsdata.fecha_entrega).format("DD-MM-YYYY")}</CMLabel></Grid>
            <Grid item sm={12}><CMLabel fontSize='16px' color='black'><b>Fecha Registro :</b> {moment(rowsdata.fecha_registro).format("DD-MM-YYYY")}</CMLabel></Grid>
            <Grid item sm={12}><CMLabel fontSize='16px' color='black'><b>OC Actualización :</b> {moment(rowsdata.ultima_fecha_modificacion).format("DD-MM-YYYY")}</CMLabel></Grid>
          </Grid>

          <Grid item sm={1} />

          <Grid item sm={5.5}>
            <CMLabel sx={{ margin: 2 }} fontSize='20px' color="black"><b>Solicitante</b></CMLabel>
            <hr />
            <Grid item sm={12}><CMLabel fontSize='16px' color='black'><b>Nombre :</b> {rowsdata.nombre_solicitante}</CMLabel></Grid>
            <Grid item sm={12}><CMLabel fontSize='16px' color='black'><b>Destino :</b> {rowsdata.destino}</CMLabel></Grid>
            <CMLabel sx={{ margin: 2 }} fontSize='20px' color="black"><b>Aprobadores</b></CMLabel>
            <hr />
            <Grid item sm={12}><CMLabel fontSize='16px' color='black'><b>Gerencia :</b> {rowsdata.nombre_aprobador_gerencia}</CMLabel></Grid>
            <Grid item sm={12}><CMLabel fontSize='16px' color='black'><b>Finanzas :</b> {rowsdata.nombre_aprobador_finanzas}</CMLabel></Grid>
            <CMLabel sx={{ margin: 2 }} fontSize='20px' color="black"><b>Importe</b></CMLabel>
            <hr />
            <Grid item sm={6} container justifyContent="space-between" alignItems="center">
              <Grid item sm={12} container justifyContent="space-between">
                <Grid><CMLabel fontSize='16px' color='black'><b>Subtotal :</b></CMLabel></Grid>
                <Grid><CMLabel fontSize='16px' color='black'>{rowsdata.total_antes_descuento ? formatCurrency(rowsdata.total_antes_descuento, rowsdata.moneda) : ''}</CMLabel></Grid>
              </Grid>
              <Grid item sm={12} container justifyContent="space-between">
                <Grid><CMLabel fontSize='16px' color='black'><b>Impuesto :</b></CMLabel></Grid>
                <Grid><CMLabel fontSize='16px' color='black'>{rowsdata.impuesto ? formatCurrency(rowsdata.impuesto, rowsdata.moneda) : formatCurrency(0, rowsdata.moneda)}</CMLabel></Grid>
              </Grid>
              <Grid item sm={12} container justifyContent="space-between">
                <Grid><CMLabel fontSize='16px' color='black'><b>Total :</b></CMLabel></Grid>
                <Grid><CMLabel fontSize='16px' color='black'>{rowsdata.total_pago_vencido ? formatCurrency(rowsdata.total_pago_vencido, rowsdata.moneda) : ''}</CMLabel></Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* ── ANÁLISIS DE LA ORDEN ──────────────────────────────────────────────── */}
        {loadingAnalisis && (
          <Grid item xs={12}>
            <Typography color="textSecondary">Cargando análisis...</Typography>
          </Grid>
        )}

        {analisis && (
          <Grid item xs={12} sx={{ mb: 2 }}>
            <div style={{
              border: `2px solid ${hayAlertas ? '#f5c6cb' : '#b7ebc0'}`,
              borderRadius: "10px",
              padding: "15px",
              backgroundColor: hayAlertas ? "#fff5f5" : "#f0fff3"
            }}>

              {/* TÍTULO */}
              <Typography variant="h6" sx={{ color: hayAlertas ? '#c0392b' : '#27ae60', mb: 1 }}>
                {hayAlertas ? '⚠️ Análisis de la Orden — Con Alertas' : '✅ Análisis de la Orden — Sin Alertas'}
              </Typography>

              {/* RESUMEN */}
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Typography variant="body2" color="textSecondary">
                    Total líneas: <b>{analisis.resumenAlertas?.totalLineas}</b>
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2" sx={{ color: hayAlertas ? '#c0392b' : '#27ae60' }}>
                    Líneas con alerta: <b>{analisis.resumenAlertas?.lineasConAlerta}</b>
                  </Typography>
                </Grid>
                {analisis.resumenAlertas?.alertasCantidadFueraRango > 0 && (
                  <Grid item>
                    <Typography variant="body2" color="error">
                      📦 Cantidad fuera de rango: <b>{analisis.resumenAlertas.alertasCantidadFueraRango}</b>
                    </Typography>
                  </Grid>
                )}
                {analisis.resumenAlertas?.alertasPrecioFueraRango > 0 && (
                  <Grid item>
                    <Typography variant="body2" color="error">
                      💰 Precio fuera de rango: <b>{analisis.resumenAlertas.alertasPrecioFueraRango}</b>
                    </Typography>
                  </Grid>
                )}
                {analisis.resumenAlertas?.alertasProveedorDiferente > 0 && (
                  <Grid item>
                    <Typography variant="body2" color="error">
                      👤 Proveedor diferente: <b>{analisis.resumenAlertas.alertasProveedorDiferente}</b>
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {/* DETALLE POR LÍNEA */}
              {analisis.lineas?.filter(l => l.tieneAlertas).map((linea, index) => (
                <div key={index} style={{
                  border: "1px solid #f5c6cb",
                  backgroundColor: "#fff",
                  padding: "12px",
                  borderRadius: "8px",
                  marginTop: "12px"
                }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>{linea.itemCode}</b> — {linea.itemName} &nbsp;
                    <span style={{ color: '#c0392b', fontSize: '12px' }}>
                      ({linea.cantAlertas} alerta{linea.cantAlertas > 1 ? 's' : ''})
                    </span>
                  </Typography>

                  {/* ── ALERTA: CANTIDAD FUERA DE RANGO ── */}
                  {linea.analisis?.cantidadFueraDeRango?.alerta && (() => {
                    const c = linea.analisis.cantidadFueraDeRango;
                    return (
                      <div style={{ backgroundColor: "#fdecea", border: "1px solid #f5c6cb", borderRadius: "6px", padding: "10px", marginBottom: "8px" }}>
                        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                          <b>📦 Cantidad fuera de rango</b>
                        </Typography>

                        {/* Fila 1: cantidades */}
                        <Grid container spacing={3} sx={{ mb: 0.5 }}>
                          <Grid item>
                            <Typography variant="body2">
                              Cantidad actual: <b>{c.cantidadBorrador}</b>
                              {c.uomBorrador && <span style={{ color: '#666', fontSize: '11px' }}> ({c.uomBorrador})</span>}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Typography variant="body2">
                              Última cantidad (OC#{c.ultimaOCNum}): <b>{c.ultimaCantidad}</b>
                              {c.uomUltimaOC && <span style={{ color: '#666', fontSize: '11px' }}> ({c.uomUltimaOC})</span>}
                            </Typography>
                          </Grid>
                          {/* Si hubo conversión de UoM, mostrar cantidad convertida */}
                          {c.uomConvertida && c.ultimaCantidadConvert != null && (
                            <Grid item>
                              <Typography variant="body2">
                                Cantidad convertida: <b>{c.ultimaCantidadConvert}</b>
                                <BadgeConversion label={`Factor OC: ${c.factorOC} / Borrador: ${c.factorBorrador}`} />
                              </Typography>
                            </Grid>
                          )}
                        </Grid>

                        {/* Fila 2: variación */}
                        <Grid container spacing={3} alignItems="center">
                          <Grid item>
                            <Typography variant="body2">
                              Variación: <b style={{ color: colorVariacion(c.variacionPct) }}>
                                {c.variacionPct > 0 ? '+' : ''}{c.variacionPct}%
                              </b>
                              <span style={{ color: '#888', fontSize: '11px' }}> (tolerancia ±{c.toleranciaPC}%)</span>
                            </Typography>
                          </Grid>
                          {c.uomConvertida && (
                            <Grid item>
                              <Chip label="UoM convertida" size="small" sx={{ backgroundColor: '#fff3cd', color: '#856404', fontSize: '11px' }} />
                            </Grid>
                          )}
                        </Grid>

                        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                          Última OC: {c.ultimaFechaOC ? moment(c.ultimaFechaOC).format('DD/MM/YYYY') : '—'}
                        </Typography>
                      </div>
                    );
                  })()}

                  {/* ── ALERTA: PRECIO FUERA DE RANGO ── */}
                  {linea.analisis?.precioFueraDeRango?.alerta && (() => {
                    const p = linea.analisis.precioFueraDeRango;
                    return (
                      <div style={{ backgroundColor: "#fdecea", border: "1px solid #f5c6cb", borderRadius: "6px", padding: "10px", marginBottom: "8px" }}>
                        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                          <b>💰 Precio fuera de rango</b>
                        </Typography>

                        {/* Fila 1: precios */}
                        <Grid container spacing={3} sx={{ mb: 0.5 }}>
                          <Grid item>
                            <Typography variant="body2">
                              Precio actual: <b>{p.precioBorrador} {p.monedaBorrador}</b>
                              {p.uomBorrador && <span style={{ color: '#666', fontSize: '11px' }}> / {p.uomBorrador}</span>}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Typography variant="body2">
                              Último precio (OC#{p.ultimaOCNum}): <b>{p.ultimoPrecio} {p.monedaUltimaOC}</b>
                              {p.uomUltimaOC && <span style={{ color: '#666', fontSize: '11px' }}> / {p.uomUltimaOC}</span>}
                            </Typography>
                          </Grid>
                          {/* Precio convertido (con UoM y/o moneda) */}
                          {p.ultimoPrecioConvert != null && (p.uomConvertida || p.monedaConvertida) && (
                            <Grid item>
                              <Typography variant="body2">
                                Precio convertido: <b>{p.ultimoPrecioConvert} {p.monedaBorrador}</b>
                                {p.uomConvertida && <BadgeConversion label={`Factor OC: ${p.factorOC} / Borrador: ${p.factorBorrador}`} />}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>

                        {/* Fila 2: tipo de cambio si aplica */}
                        {p.monedaConvertida && p.tcUsado != null && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Tipo de cambio aplicado: <b>{p.tcUsado}</b>
                            <span style={{ color: '#666', fontSize: '11px' }}> — {p.tcOrigen}</span>
                          </Typography>
                        )}

                        {/* Fila 3: variación */}
                        <Grid container spacing={3} alignItems="center">
                          <Grid item>
                            <Typography variant="body2">
                              Variación: <b style={{ color: colorVariacion(p.variacionPct) }}>
                                {p.variacionPct > 0 ? '+' : ''}{p.variacionPct}%
                              </b>
                              <span style={{ color: '#888', fontSize: '11px' }}> (tolerancia ±{p.toleranciaPC}%)</span>
                            </Typography>
                          </Grid>
                          {p.uomConvertida && (
                            <Grid item>
                              <Chip label="UoM convertida" size="small" sx={{ backgroundColor: '#fff3cd', color: '#856404', fontSize: '11px' }} />
                            </Grid>
                          )}
                          {p.monedaConvertida && (
                            <Grid item>
                              <Chip label="Moneda convertida" size="small" sx={{ backgroundColor: '#cce5ff', color: '#004085', fontSize: '11px' }} />
                            </Grid>
                          )}
                        </Grid>

                        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                          Última OC: {p.ultimaFechaOC ? moment(p.ultimaFechaOC).format('DD/MM/YYYY') : '—'}
                        </Typography>
                      </div>
                    );
                  })()}

                  {/* ── ALERTA: PROVEEDOR DIFERENTE ── */}
                  {linea.analisis?.proveedorHabitual?.alerta && (() => {
                    const pv = linea.analisis.proveedorHabitual;
                    return (
                      <div style={{ backgroundColor: "#fdecea", border: "1px solid #f5c6cb", borderRadius: "6px", padding: "10px", marginBottom: "8px" }}>
                        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                          <b>👤 Proveedor diferente al habitual</b>
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item>
                            <Typography variant="body2">
                              Proveedor actual: <b>{pv.proveedorBorrador}</b>
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Typography variant="body2">
                              Proveedor habitual: <b>{pv.proveedorFrecuente}</b> — {pv.proveedorFrecuenteNombre}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Typography variant="body2">
                              OCs consolidadas: <b>{pv.ocsConsolidadas}</b>
                            </Typography>
                          </Grid>
                          {pv.ultimaOCNum && (
                            <Grid item>
                              <Typography variant="body2">
                                Última OC de referencia: <b>#{pv.ultimaOCNum}</b>
                                {pv.ultimaFechaOC && <span style={{ color: '#666', fontSize: '11px' }}> — {moment(pv.ultimaFechaOC).format('DD/MM/YYYY')}</span>}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </div>
                    );
                  })()}

                </div>
              ))}

            </div>
          </Grid>
        )}

        {/* GRID DE DETALLE */}
        <Grid container item xs={12} sm={12}>
          <Grid style={{ height: 400, width: '100%', overflowX: 'auto' }} sx={{ py: 0 }}>
            {rowsdata.tipo === 'Articulo'
              ? <DataGrid
                  rows={rowsgrid} columns={columns} pageSize={15} rowsPerPageOptions={[15]}
                  sx={dataGridSx} pagination rowHeight={50}
                  processRowUpdate={processRowUpdate}
                  onProcessRowUpdateError={handleProcessRowUpdateError}
                  experimentalFeatures={{ newEditingApi: true }}
                />
              : <DataGrid
                  rows={rowsgrid} columns={columns2} pageSize={15} rowsPerPageOptions={[15]}
                  sx={dataGridSx} pagination rowHeight={50}
                  processRowUpdate={processRowUpdate}
                  onProcessRowUpdateError={handleProcessRowUpdateError}
                  experimentalFeatures={{ newEditingApi: true }}
                  loading={cargando}
                />
            }
            {!!snackbar && (
              <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={handleCloseSnackbar} autoHideDuration={6000}>
                <Alert {...snackbar} onClose={handleCloseSnackbar} />
              </Snackbar>
            )}
          </Grid>

          {/* COMENTARIO + ACCIONES */}
          <Grid item xs={12} sm={12} container>
            <Grid item xs={12} sm={8} container alignItems="center">
              <Grid item xs={6} sm={1.5}>
                <CMLabel fontSize='16px' color='black'>Comentario: </CMLabel>
              </Grid>
              <Grid item xs={6} sm={6}>
                <CMTextField
                  name="comment" label='Comentario' value={comment}
                  disabled={disabledComment} onChange={handleComentarioChange} fullWidth
                />
              </Grid>
            </Grid>
            <Grid item xs={12} sm={4} container justifyContent="flex-end" alignItems="center">
              <Grid item xs={8}>
                <InputLabel>Seleccionar opción de Orden de {rowsdata.tipo === 'Articulo' ? 'Compra' : 'Servicio'}</InputLabel>
                <Select value={selectedOption} onChange={handleChange} fullWidth>
                  <MenuItem value="aprobar">Aprobar</MenuItem>
                  <MenuItem value="rechazar">Rechazar</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={4} container justifyContent="flex-end" alignItems="center">
                <CMButton
                  label="Registrar" variant="contained"
                  sx={{ marginTop: '10px' }}
                  onClick={hanbleEntregada}
                  disabled={isDisabled}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

      </Grid>
    </Grid>
  );
};

export default OrdenesPendientesAprobarFinanzaVer;